import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

export default function PriceVisibilityButton({ onChange, publicPrice, disabled = false }: {
  onChange: () => Promise<void>; publicPrice: boolean; disabled?: boolean;
}) {
  const pending = useRef(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!error) return;
    const timer = window.setTimeout(() => setError(''), 3000);
    return () => window.clearTimeout(timer);
  }, [error]);
  const disclose = async () => {
    if (disabled || pending.current) return;
    pending.current = true; setSaving(true); setError('');
    try { await onChange(); }
    catch { setError('가격 표시를 변경하지 못했습니다. 다시 시도해주세요.'); }
    finally { pending.current = false; setSaving(false); }
  };
  return <Control $publicPrice={publicPrice}>
    <button type="button" disabled={disabled || saving} aria-busy={saving} onClick={() => void disclose()}>
      {saving ? '변경 중…' : publicPrice ? '가격 비공개' : '가격 공개'}
    </button>
    {error && <p role="alert">{error}</p>}
  </Control>;
}

const Control = styled.div<{ $publicPrice: boolean }>`
  min-width: 0; flex-shrink: 0; margin: 4px 0 4px auto; display: flex; flex-direction: column; align-items: flex-end;
  && button {
    min-height: 32px; min-width: 80px; padding: 4px 10px; border: 1px solid var(--studio-link, var(--color-text-link));
    border-radius: var(--radius-sm, 4px); background: ${({ $publicPrice }) => $publicPrice ? 'transparent' : 'var(--admin-theme-accent-soft, var(--color-brand-subtle))'};
    color: var(--studio-link, var(--color-text-link)); font: inherit; font-size: 13px; font-weight: 600; white-space: nowrap; cursor: pointer;
  }
  && button:hover:not(:disabled) { background: var(--admin-theme-accent-soft, var(--color-brand-subtle)); }
  && button:focus-visible { outline: 2px solid var(--studio-link, var(--color-focus-ring)); outline-offset: 2px; }
  && button:disabled { background: var(--studio-raised, var(--color-bg-muted)); color: var(--studio-muted, var(--color-text-secondary)); cursor: default; }
  p { position: relative; max-width: 180px; margin: 8px 0 0; padding-bottom: 6px; color: var(--studio-danger, var(--color-danger)); font-size: 13px; line-height: 1.5; white-space: normal; }
  p::after { content: ''; position: absolute; left: 0; right: 0; bottom: 0; height: 3px; background: currentColor; transform-origin: left; animation: price-visibility-countdown 3s linear forwards; }
  @keyframes price-visibility-countdown { from { transform: scaleX(1); } to { transform: scaleX(0); } }
  @media (pointer: coarse) { && button { min-height: 44px; } }
`;
